/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import { syncableAccountUpdateProps } from "../../../graphql/account/accountCRUD";

import {
  Backdrop,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  makeStyles,
  Switch,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  ArrayInput,
  BooleanInput,
  Edit,
  email,
  FileInput,
  FormDataConsumer,
  FormTab,
  maxLength,
  minLength,
  minValue,
  number,
  NumberInput,
  RadioButtonGroupInput,
  regex,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";
import setFieldData from "final-form-set-field-data";

import {
  ACCEPTED_FILES,
  AccountType,
  CountryList,
  ExternalStatus,
  InvoiceSubjectTokens,
  MAX_FILES,
  REGEX_MULTI_EMAIL,
  REGEX_PHONE,
  TrueOrFalse,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  hasExternalId,
  sanitizeZapierData,
  triggerEvent,
} from "../../../utils/zapierUtils";
import { logError } from "../../../utils/appUtils";
import { getBackPath } from "../../../utils/utils";

import { AttachFileIcon } from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FileField } from "../../../components/common/FileField";
import { HelpButton } from "../../../components/common/HelpButton";
import { HistoryTab } from "../../../components/common/HistoryTab";
import CustomFieldsInput from "../../../components/common/CustomFieldsInput";
import { ReSyncButton } from "../../../components/common/ReSyncButton";
import { TokenInput } from "../../../components/common/TokenInput";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";

import { AccountEditHelp } from "./AccountHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { ArchiveRestoreButton } from "./ArchiveRestoreButton";
import { DialogSaveButton } from "./DialogSaveButton";
import { ContactTab } from "./AccountContactTab";
import { AccountSettingsTab } from "./AccountSettingsTab";
import { ServicesTab } from "../services/ServicesTab";
import { ChargeRulesTab } from "./chargeRules/ChargeRulesTab";
import { ProductCatalogTab } from "./ProductCatalogTab";
import {
  normalizeWebsiteUrl,
  requiredEmail,
  setAccountSyncFailed,
  setAccountSyncPending,
} from "./AccountUtils";

const useStyles = makeStyles((theme) => ({
  arrayInput: {
    marginTop: theme.spacing(-2),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();
const validateMultiEmail = [regex(REGEX_MULTI_EMAIL, "Must be valid email(s)")];
const validateSyncedBillingEmail = [requiredEmail, ...validateMultiEmail];
const validateOptEmail = [email()];
const validateOptPhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];
const validateState = [required(), minLength(2), maxLength(2)];
const validateZip = [required(), minLength(5)];

const validateTermExists = (value, allValues) => {
  const termList = window.appUser?.reloadYard?.paymentTerms?.items || [];

  const isValid = termList.some((term) => term.id === value);
  if (!isValid) return "Required. Term no longer exists";
  return undefined; // valid
};
const validatePaymentTerms = [required(), validateTermExists];
const validateCreditLimit = [number(), minValue(0)];

const BillingSetupTab = ({ record, classes, isSyncEnabled, ...props }) => {
  const form = useForm();
  const notify = useNotify();
  const [copyContactInfo, setCopyContactInfo] = useState(false);
  const isSynced = isSyncEnabled && hasExternalId(record);

  const termList = window.appUser?.reloadYard?.paymentTerms?.items || [];

  const handleCopyContactInfoChange = (event, formData) => {
    form.change(
      "billingAddress",
      event.target.checked ? formData.address : record.billingAddress,
    );
    setCopyContactInfo(event.target.checked);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} md={3}>
            <RadioButtonGroupInput
              source="skipInvoice"
              label="Skip Billing"
              choices={TrueOrFalse}
              defaultValue={false}
              row
              helperText={false}
              style={{ marginTop: 0 }}
            />
          </Grid>

          {!formData.skipInvoice && (
            <Grid container item direction="row" spacing={2} lg={10}>
              <Grid item xs={12} md={4}>
                <TextInput
                  source="einNumber"
                  label="EIN/Tax ID Number"
                  variant="outlined"
                  fullWidth
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SelectInput
                  source="paymentTermId"
                  label="Payment Terms"
                  choices={termList}
                  validate={validatePaymentTerms}
                  fullWidth
                  variant="outlined"
                  helperText={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  source="creditLimit"
                  label="Credit Limit"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"> $ </InputAdornment>
                    ),
                    inputMode: "decimal",
                  }}
                  placeholder="No limit"
                  validate={validateCreditLimit}
                  helperText={false}
                />
                <SmartTipIcon label="credit-limit-icon" mt={-1} />
              </Grid>

              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Email Settings
                    </Typography>
                  </legend>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        // The sync flag settles after the form registers; a
                        // remount re-runs validation against the new rules.
                        key={isSynced ? "billingEmail-synced" : "billingEmail"}
                        source="billingEmail"
                        label="Billing email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                        validate={
                          isSynced
                            ? validateSyncedBillingEmail
                            : validateMultiEmail
                        }
                        helperText="Comma-separated email(s)"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TokenInput
                        source="invoiceSubject"
                        label="Email Subject"
                        validate={validateRequired}
                        availableTokens={InvoiceSubjectTokens}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box mt={1}>
                        <BooleanInput
                          source="invoiceAttachments"
                          color="primary"
                          label="Enclose Attachments"
                          helperText={false}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>

              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Billing Address
                    </Typography>
                  </legend>
                  <Grid container spacing={2}>
                    {!record.billingAddress && formData.address && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Same as Company Address"
                          style={{ marginLeft: "0px" }}
                          control={
                            <Switch
                              name="copyContactInfo"
                              size="small"
                              checked={copyContactInfo}
                              disabled={!formData.address || !formData.email}
                              onChange={(event) =>
                                handleCopyContactInfoChange(event, formData)
                              }
                            />
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="businessName"
                        label="Business Name"
                        variant="outlined"
                        fullWidth
                        disabled
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="billingAddress.street"
                        label="Street Address"
                        variant="outlined"
                        fullWidth
                        validate={validateRequired}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextInput
                        source="billingAddress.city"
                        label="City"
                        variant="outlined"
                        fullWidth
                        validate={validateRequired}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <TextInput
                        source="billingAddress.state"
                        label="State"
                        variant="outlined"
                        fullWidth
                        parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                        validate={validateState}
                        helperText={false}
                        inputProps={{ maxLength: 2 }}
                      />
                    </Grid>
                    <Grid item xs={8} md={2}>
                      <TextInput
                        source="billingAddress.zipCode"
                        label="Zip Code"
                        variant="outlined"
                        fullWidth
                        validate={validateZip}
                        helperText={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <SelectInput
                        source="billingAddress.country"
                        label="Country"
                        variant="outlined"
                        fullWidth
                        allowEmpty
                        choices={CountryList}
                        validate={validateRequired}
                        helperText={false}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CUSTOM FIELDS
                    </Typography>
                  </legend>
                  <CustomFieldsInput
                    {...props}
                    source="invoiceCustomFields"
                    displayValues={[]}
                    className={classes.arrayInput}
                  />
                </fieldset>
              </Grid>
            </Grid>
          )}
          {!formData.skipInvoice && (
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      BILLING CONTACTS
                    </Typography>
                  </legend>
                  <ArrayInput
                    source="billingContacts"
                    label=""
                    fullWidth
                    className={classes.arrayInput}
                  >
                    <SimpleFormIterator>
                      <FormDataConsumer>
                        {({
                          formData, // The whole form data
                          scopedFormData, // The data for this item of the ArrayInput
                          getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                          ...rest
                        }) => (
                          <Grid container direction="row" spacing={2}>
                            <Grid item xs={12} lg={3}>
                              <TextInput
                                source={getSource("name")}
                                label="Contact Name"
                                variant="outlined"
                                fullWidth
                                validate={validateRequired}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                              <TextInput
                                source={getSource("email")}
                                label="Email"
                                type="email"
                                variant="outlined"
                                fullWidth
                                parse={(v) =>
                                  v !== "" ? v.toLowerCase() : undefined
                                }
                                validate={validateOptEmail}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextInput
                                source={getSource("phone")}
                                label="Phone"
                                variant="outlined"
                                fullWidth
                                validate={validateOptPhone}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextInput
                                source={getSource("description")}
                                label="Description"
                                variant="outlined"
                                fullWidth
                                helperText={false}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </FormDataConsumer>
                    </SimpleFormIterator>
                  </ArrayInput>
                </fieldset>
              </Grid>
            </Grid>
          )}
          <Grid item container direction="row" spacing={0} md={12} xs={false}>
            <Grid item xs={12}>
              <FileInput
                source="attachments"
                label="ATTACHMENTS"
                variant="outlined"
                fullWidth
                className={classes.fileInput}
                placeholder={
                  <p>
                    <AttachFileIcon fontSize="large" /> <br /> Drop file(s) to
                    upload, or click to select.
                  </p>
                }
                accept={ACCEPTED_FILES}
                multiple
                maxFiles={MAX_FILES}
                options={{ maxFiles: MAX_FILES }}
                onChange={(value) =>
                  handleFileChange("attachments", value, form, notify)
                }
              >
                <FileField source="src" title="title" target="_blank" />
              </FileInput>
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const AccountEditToolbar = ({ classes, ...props }) => {
  const { isSyncEnabled, record } = props;
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isLocked =
    record?.externalStatus === ExternalStatus.SYNC_PENDING ||
    record?.externalStatus === ExternalStatus.SYNC_FAILED;

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogSaveButton
        {...props}
        label="Save"
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        isDeleting={isDeleting}
        isSyncEnabled={isSyncEnabled}
        isLocked={isLocked}
      />
      <div style={{ display: "flex" }}>
        <DeepDeleteButton
          {...props}
          isDeleting={isDeleting}
          setIsDeleting={setIsDeleting}
          isSaving={isSaving}
        />
        <ArchiveRestoreButton
          {...props}
          classes={classes}
          isDeleting={isDeleting}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          isSyncEnabled={isSyncEnabled}
          isLocked={isLocked}
        />
      </div>
    </Toolbar>
  );
};

const AccountEditActions = (props) => {
  const { data, isSyncEnabled } = props;
  const handleResync = async (record) => {
    try {
      await setAccountSyncPending(record);
      const sanitizedData = sanitizeZapierData(
        syncableAccountUpdateProps,
        data,
      );
      const normalizedData = {
        ...sanitizedData,
        website: normalizeWebsiteUrl(sanitizedData.website),
      };

      if (hasExternalId(record)) {
        triggerEvent(normalizedData, "accountUpdated");
      } else {
        // await here to give more buffering time.
        await triggerEvent(normalizedData, "accountCreated");
      }
    } catch (error) {
      setAccountSyncFailed(record);
      logError("accounts:AccountEdit:handleResync", error);
    }
  };

  return (
    <TopToolbar>
      <HelpButton>
        <AccountEditHelp {...props} />
      </HelpButton>
      <ReSyncButton
        record={data}
        isSyncEnabled={isSyncEnabled}
        syncFn={handleResync}
      />
      <BackButton {...props} />
    </TopToolbar>
  );
};

// Rendered by <Edit>, which clones it with record, save, saving, version,
// redirect, basePath and resource once the record is loaded. Owning the form
// here means one edit controller and one GET_ONE, and the form is not torn
// down while the controller refetches.
const AccountEditForm = ({
  record,
  isSyncEnabled,
  setIsSyncEnabled,
  backPath,
  ...formProps
}) => {
  const classes = useStyles();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const [allServices, setAllServices] = useState([]);
  const [allChargeRules, setAllChargeRules] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  // <Edit> injects the form plumbing; the tabs get only the route props, and
  // the route props must not reach TabbedForm (it spreads the rest onto the
  // DOM <form>).
  const { save, saving, version, redirect, undoable, mutationMode, ...props } =
    formProps;
  const formPlumbing = {
    save,
    saving,
    version,
    redirect,
    undoable,
    mutationMode,
    basePath: props.basePath,
    resource: props.resource,
  };

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("accountUpdated");
      // account must be imported or synced to be syncable
      setIsSyncEnabled(triggerEnabled && hasExternalId(record));
    };
    if (record) checkSyncEnabled();
  }, [record]);

  return (
    <TabbedForm
      {...formPlumbing}
      record={record}
      variant="outlined"
      submitOnEnter={false}
      toolbar={
        <AccountEditToolbar
          {...props}
          isSyncEnabled={isSyncEnabled}
          classes={classes}
          backPath={backPath}
        />
      }
      mutators={{ setFieldData }}
    >
      <FormTab path="" label="Contact Info">
        <ContactTab {...props} record={record} />
      </FormTab>
      {mdUp && (
        <FormTab path="invoiceSetup" label="Billing Setup">
          <BillingSetupTab
            {...props}
            record={record}
            classes={classes}
            isSyncEnabled={isSyncEnabled}
          />
        </FormTab>
      )}
      <FormTab path="serviceRates" label="Service Rates">
        <ServicesTab
          {...props}
          account={record}
          allServices={allServices}
          setAllServices={setAllServices}
          backPath={backPath}
        />
      </FormTab>
      <FormTab path="billingRules" label="Billing Rules">
        <ChargeRulesTab
          {...props}
          account={record}
          allChargeRules={allChargeRules}
          setAllChargeRules={setAllChargeRules}
          allServices={allServices}
          backPath={backPath}
        />
      </FormTab>
      {mdUp && (
        <FormTab path="customSettings" label="Custom Settings">
          <AccountSettingsTab {...props} record={record} />
        </FormTab>
      )}
      {mdUp && (
        <FormTab path="productCatalog" label="Product Catalog">
          <ProductCatalogTab
            {...props}
            account={record}
            allProducts={allProducts}
            setAllProducts={setAllProducts}
            classes={classes}
            backPath={backPath}
          />
        </FormTab>
      )}
      {lgUp && (
        <FormTab path="historyLog" label="History Log">
          <HistoryTab />
        </FormTab>
      )}
    </TabbedForm>
  );
};

export const AccountEdit = (props) => {
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("accountUpdated"),
  );
  const backPath = getBackPath(props);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Account" />}
      mutationMode="pessimistic"
      actions={
        <AccountEditActions
          {...props}
          isSyncEnabled={isSyncEnabled}
          backPath={backPath}
        />
      }
    >
      <AccountEditForm
        {...props}
        isSyncEnabled={isSyncEnabled}
        setIsSyncEnabled={setIsSyncEnabled}
        backPath={backPath}
      />
    </Edit>
  );
};
